import React, {useEffect, type ReactNode} from 'react';
import useDocusaurusContext from '@docusaurus/useDocusaurusContext';
import {SupportWidget, type SupportSession} from '@mupag/support-widget';
import {supportSessionForOrigin} from '../../scripts/support-config.cjs';
import '@mupag/support-widget/styles.css';

type RootProps = {
  children: ReactNode;
};

function enhanceOpenApiAccessibility(): void {
  const isEnglish = document.documentElement.lang.toLowerCase().startsWith('en');
  const arrowLabels = {
    left: isEnglish ? 'Scroll tabs left' : 'Rolar abas para a esquerda',
    right: isEnglish ? 'Scroll tabs right' : 'Rolar abas para a direita',
  };

  document.querySelectorAll<HTMLButtonElement>('.openapi-tabs__arrow.left').forEach((button) => {
    button.setAttribute('aria-label', arrowLabels.left);
  });
  document.querySelectorAll<HTMLButtonElement>('.openapi-tabs__arrow.right').forEach((button) => {
    button.setAttribute('aria-label', arrowLabels.right);
  });

  document
    .querySelectorAll<HTMLElement>('.openapi-schema__list-item, .openapi-params__list-item')
    .forEach((item) => {
      if (item.parentElement?.matches('ul, ol')) {
        item.setAttribute('role', 'listitem');
      }
    });

  document.querySelectorAll<HTMLElement>('.openapi-security__summary-header').forEach((heading) => {
    heading.setAttribute('role', 'heading');
    heading.setAttribute('aria-level', '3');
  });
}

export default function Root({children}: RootProps): React.ReactElement {
  const {siteConfig, i18n} = useDocusaurusContext();
  const locale = i18n.currentLocale === 'en' ? 'en' : 'pt-BR';
  const support = siteConfig.customFields?.support as SupportSession | undefined;
  useEffect(() => {
    enhanceOpenApiAccessibility();

    const observer = new MutationObserver(enhanceOpenApiAccessibility);
    observer.observe(document.body, {childList: true, subtree: true});

    return () => observer.disconnect();
  }, []);

  return <>{children}<SupportWidget identityKey={`docs:${locale}`} locale={locale}
    loadSession={async () => supportSessionForOrigin(support, window.location.origin, siteConfig.url)} /></>;
}
